import React, { useState, useRef, useEffect } from 'react';
import './AgeGate.css';
import analyticsService from './services/AnalyticsService.ts';

export default function AgeGate({ onVerified }: { onVerified: () => void }) {
  const [isFading, setIsFading] = useState(false);
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [error, setError] = useState('');
  const [isInvalidAge, setIsInvalidAge] = useState(false);
  
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    monthRef.current?.focus();
  }, []);

  // Keep keyboard focus inside the dialog while the page renders underneath it
  const handleTrapKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>('input, button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const calculateAge = (year: number, month: number, day: number): number => {
    const today = new Date();
    const birthDate = new Date(year, month - 1, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const checkBirthdayValidity = () => {
    // Only check if all fields have some content and year is complete
    if (birthMonth && birthDay && birthYear && birthYear.length === 4) {
      const month = parseInt(birthMonth, 10);
      const day = parseInt(birthDay, 10);
      const year = parseInt(birthYear, 10);
      
      // Use same validation logic as validateAndSubmit but without side effects
      if (month && day && year && 
          month >= 1 && month <= 12 && 
          day >= 1 && day <= 31 && 
          year >= 1900 && year <= new Date().getFullYear()) {
        
        // Check if date is valid (e.g., not Feb 31) - same logic as validateAndSubmit
        const testDate = new Date(year, month - 1, day);
        if (testDate.getMonth() === month - 1) {
          // Use same age calculation as validateAndSubmit
          const age = calculateAge(year, month, day);
          if (age < 21) {
            setIsInvalidAge(true);
          } else {
            setIsInvalidAge(false);
          }
          return;
        }
      }
    }
    // Reset invalid state if incomplete or invalid date
    setIsInvalidAge(false);
  };

  const handleMonthChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) {
      // Preserve leading zeros
      setBirthMonth(cleaned);
      
      // Auto-advance to day field when month is complete
      if (cleaned.length === 2) {
        const monthNum = parseInt(cleaned, 10);
        if (monthNum >= 1 && monthNum <= 12) {
          setTimeout(() => dayRef.current?.focus(), 0);
        }
      }
      
      // Check validity after state update
      setTimeout(checkBirthdayValidity, 0);
    }
  };

  const handleDayChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) {
      // Preserve leading zeros
      setBirthDay(cleaned);
      
      // Auto-advance to year field when day is complete
      if (cleaned.length === 2) {
        const dayNum = parseInt(cleaned, 10);
        if (dayNum >= 1 && dayNum <= 31) {
          setTimeout(() => yearRef.current?.focus(), 0);
        }
      }
      
      // Check validity after state update
      setTimeout(checkBirthdayValidity, 0);
    }
  };

  const handleYearChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      setBirthYear(cleaned);
      
      // Auto-advance if complete valid birthday over 21 is entered
      if (cleaned.length === 4 && birthMonth && birthDay) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          const month = parseInt(birthMonth, 10);
          const day = parseInt(birthDay, 10);
          const year = parseInt(cleaned, 10);
          
          // Only auto-advance if validateAndSubmit passes (this ensures all validation is centralized)
          if (validateAndSubmit(month, day, year)) {
            // validateAndSubmit handles all validation and age checking
          }
        }, 50);
      }
      
      // Check validity after state update
      setTimeout(checkBirthdayValidity, 0);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, field: 'month' | 'day' | 'year') => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Try to parse full date formats (MM/DD/YYYY, MM-DD-YYYY, etc.)
    const datePattern = /^(\d{1,2})[/\-.\s](\d{1,2})[/\-.\s](\d{2,4})$/;
    const match = pastedText.match(datePattern);
    
    if (match) {
      // Full date pasted
      const [, month, day, year] = match;
      const fullYear = year.length === 2 ? `20${year}` : year;
      setBirthMonth(month.padStart(2, '0'));
      setBirthDay(day.padStart(2, '0'));
      setBirthYear(fullYear);
      yearRef.current?.focus();
      
      // Auto-advance if pasted date is valid and over 21
      setTimeout(() => {
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(fullYear, 10);
        
        // Only auto-advance if validateAndSubmit passes (ensures all validation is centralized)
        if (validateAndSubmit(monthNum, dayNum, yearNum)) {
          // validateAndSubmit handles all validation and age checking
        }
      }, 100);
    } else {
      // Single field paste
      const cleaned = pastedText.replace(/\D/g, '');
      if (field === 'month' && cleaned.length <= 2) {
        setBirthMonth(cleaned.padStart(2, '0'));
      } else if (field === 'day' && cleaned.length <= 2) {
        setBirthDay(cleaned.padStart(2, '0'));
      } else if (field === 'year') {
        if (cleaned.length === 2) {
          // Assume 20xx for 2-digit years
          setBirthYear(`20${cleaned}`);
        } else if (cleaned.length === 4) {
          setBirthYear(cleaned);
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: 'month' | 'day' | 'year') => {
    // Handle backspace navigation between fields
    if (e.key === 'Backspace') {
      if (field === 'day' && birthDay === '') {
        e.preventDefault();
        monthRef.current?.focus();
        // Move cursor to end of month field
        setTimeout(() => {
          if (monthRef.current) {
            monthRef.current.setSelectionRange(birthMonth.length, birthMonth.length);
          }
        }, 0);
      } else if (field === 'year' && birthYear === '') {
        e.preventDefault();
        dayRef.current?.focus();
        // Move cursor to end of day field
        setTimeout(() => {
          if (dayRef.current) {
            dayRef.current.setSelectionRange(birthDay.length, birthDay.length);
          }
        }, 0);
      }
    }
    // Tab key should trigger auto-advance if field has valid content
    else if (e.key === 'Tab' && !e.shiftKey) {
      if (field === 'month' && birthMonth.length === 2) {
        const monthNum = parseInt(birthMonth, 10);
        if (monthNum >= 1 && monthNum <= 12) {
          // Already valid, let Tab naturally advance
          return;
        }
      } else if (field === 'day' && birthDay.length === 2) {
        const dayNum = parseInt(birthDay, 10);
        if (dayNum >= 1 && dayNum <= 31) {
          // Already valid, let Tab naturally advance
          return;
        }
      }
    }
    // Handle arrow keys for better navigation
    else if (e.key === 'ArrowLeft') {
      if (field === 'day' && (e.currentTarget.selectionStart === 0)) {
        e.preventDefault();
        monthRef.current?.focus();
        setTimeout(() => {
          if (monthRef.current) {
            monthRef.current.setSelectionRange(birthMonth.length, birthMonth.length);
          }
        }, 0);
      } else if (field === 'year' && (e.currentTarget.selectionStart === 0)) {
        e.preventDefault();
        dayRef.current?.focus();
        setTimeout(() => {
          if (dayRef.current) {
            dayRef.current.setSelectionRange(birthDay.length, birthDay.length);
          }
        }, 0);
      }
    }
    else if (e.key === 'ArrowRight') {
      const cursorAtEnd = e.currentTarget.selectionStart === e.currentTarget.value.length;
      if (field === 'month' && cursorAtEnd && birthMonth.length > 0) {
        e.preventDefault();
        dayRef.current?.focus();
        setTimeout(() => {
          if (dayRef.current) {
            dayRef.current.setSelectionRange(0, 0);
          }
        }, 0);
      } else if (field === 'day' && cursorAtEnd && birthDay.length > 0) {
        e.preventDefault();
        yearRef.current?.focus();
        setTimeout(() => {
          if (yearRef.current) {
            yearRef.current.setSelectionRange(0, 0);
          }
        }, 0);
      }
    }
  };

  const validateAndSubmit = (month: number, day: number, year: number) => {
    setError('');

    // Critical security check: ensure all inputs are valid numbers
    if (!month || !day || !year || isNaN(month) || isNaN(day) || isNaN(year)) {
      setError('Please enter a valid date');
      return false;
    }

    if (month < 1 || month > 12) {
      setError('Please enter a valid month (1-12)');
      return false;
    }

    if (day < 1 || day > 31) {
      setError('Please enter a valid day (1-31)');
      return false;
    }

    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      setError('Please enter a valid year');
      return false;
    }

    // Check if date is valid (e.g., not Feb 31)
    const testDate = new Date(year, month - 1, day);
    if (testDate.getMonth() !== month - 1) {
      setError('Please enter a valid date');
      return false;
    }

    const age = calculateAge(year, month, day);

    if (age < 21) {
      // Track age verification rejection
      analyticsService.trackEvent('age_verification', {
        verified: false,
        page_path: window.location.pathname,
        verification_method: 'date_of_birth'
      });
      
      // Enhanced age verification tracking
      analyticsService.trackAgeVerification({
        verification_method: 'date_of_birth',
        compliance_state: 'rejected',
        user_age_range: 'under_21'
      });
      
      setError('You must be 21 or older to enter this site');
      return false;
    } else {
      // Track age verification
      analyticsService.trackEvent('age_verification', {
        verified: true,
        page_path: window.location.pathname,
        verification_method: 'date_of_birth'
      });
      
      // Enhanced age verification tracking
      analyticsService.trackAgeVerification({
        verification_method: 'date_of_birth',
        compliance_state: 'verified',
        user_age_range: 'over_21'
      });
      
      setIsFading(true);
      setTimeout(() => {
        sessionStorage.setItem('ageGatePassed', 'true');
        onVerified();
      }, 500); // Match this with the CSS transition duration
      return true;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Parse with base 10 to handle leading zeros correctly
    const month = parseInt(birthMonth, 10);
    const day = parseInt(birthDay, 10);
    const year = parseInt(birthYear, 10);
    validateAndSubmit(month, day, year);
  };

  const handleExit = () => {
    analyticsService.trackEvent('age_verification', {
      verified: false,
      page_path: window.location.pathname,
      verification_method: 'exit_button'
    });
    window.location.href = 'https://www.google.com';
  };

  return (
    <div className={`age-gate ${isFading ? 'fade-out' : ''}`}>
      <div
        className="age-gate-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        ref={modalRef}
        onKeyDown={handleTrapKeyDown}
      >
        <h2 id="age-gate-title">Find Your Sweetspot!</h2>
        <p className="gate-instruction">Please enter your date of birth to verify your age</p>

        <form onSubmit={handleSubmit} className="date-form">
            <div className="date-inputs">
              <input
                ref={monthRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="MM"
                maxLength={2}
                value={birthMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'month')}
                onPaste={(e) => handlePaste(e, 'month')}
                className="date-input month"
                aria-label="Birth month"
                autoComplete="bday-month"
              />
              <span className="date-separator">/</span>
              <input
                ref={dayRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="DD"
                maxLength={2}
                value={birthDay}
                onChange={(e) => handleDayChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'day')}
                onPaste={(e) => handlePaste(e, 'day')}
                className="date-input day"
                aria-label="Birth day"
                autoComplete="bday-day"
              />
              <span className="date-separator">/</span>
              <input
                ref={yearRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="YYYY"
                maxLength={4}
                value={birthYear}
                onChange={(e) => handleYearChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'year')}
                onPaste={(e) => handlePaste(e, 'year')}
                className="date-input year"
                aria-label="Birth year"
                autoComplete="bday-year"
              />
            </div>
            
            {(error || isInvalidAge) && (
              <div className="error-message" role="alert">
                {error || 'You must be 21 or older to enter this site'}
              </div>
            )}

            <div className="gate-actions">
              <button
                type="submit"
                className={`submit-button ${isInvalidAge ? 'disabled' : ''}`}
                disabled={isInvalidAge}
              >
                Submit
              </button>
              <button type="button" className="exit-button" onClick={handleExit}>
                No, exit
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}